import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAppState } from "../state";

// Components
import { RegionSelectModal } from "../components/RegionSelectModal/RegionSelectModal";

// Hooks
import useUserLocation from "../hooks/useUserLocation/useUserLocation";
import useSupportedLocations from "../hooks/useSupportedLocations/useSupportedLocations";

// Utils
import { selectRegionBasedOnUserLanguages } from "../utils/locale";
import { CITY_CONSTANTS, reverseMapping } from "../constants";

const RegionContext = createContext();

export function useRegion() {
  return useContext(RegionContext);
}

export const getRegion = () => {
  const storedData = localStorage.getItem("userRegion");
  try {
    if (storedData && storedData.startsWith("{")) {
      const parsed = JSON.parse(storedData);
      return parsed.region;
    }
    return (
      storedData ||
      (window.location.host === "us.trubeapp.com" ||
      window.location.pathname.startsWith("/us")
        ? "US"
        : "GB")
    );
  } catch (e) {
    return window.location.host === "us.trubeapp.com" ||
      window.location.pathname.startsWith("/us")
      ? "US"
      : "GB";
  }
};

export const getCity = () => {
  const storedData = localStorage.getItem("userRegion");
  try {
    if (storedData && storedData.startsWith("{")) {
      const parsed = JSON.parse(storedData);
      return parsed.city || null;
    }
    return null;
  } catch (e) {
    return null;
  }
};

const initialRegion = getRegion();
const initialCity = getCity();

export function RegionProvider({ children }) {
  const { locations, loading: locationsLoading } = useSupportedLocations();

  const { user, hasTriedToAutoLogin, setProductsLoaded } = useAppState();
  const { userLocation, userCity } = useUserLocation(user); // Location auto-detect

  const [region, setRegion] = useState(initialRegion);
  const [city, setCity] = useState(initialCity);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Determine the initial region based on user status and userRegion
  const userRegionData = useMemo(() => {
    if (user && hasTriedToAutoLogin) {
      // Дефолтим для новых пользователей
      return {
        region: user.region,
        city:
          user.city ||
          (user.region === "GB"
            ? "LONDON"
            : (initialRegion === "US" && initialCity) || "LOS_ANGELES"),
      };
    }
    if (userLocation) {
      // там своя логика определения города по умолчанию??
      // тут можем так установить из `initialCity`? или лучше city?
      return { region: userLocation, city: userCity || city };
      // return { region: userLocation, city: userCity || null };
    }
    return { region: getRegion(), city: getCity() };
  }, [user, hasTriedToAutoLogin, userLocation, userCity]);

  // Update region only after hasTriedToAutoLogin is true
  useEffect(() => {
    setRegion(userRegionData.region /*user.region*/);
    setCity(userRegionData.city);
    localStorage.setItem("userRegion", JSON.stringify(userRegionData));
    setProductsLoaded(false);
  }, [userRegionData]);

  const handleRegionSelect = (regionData) => {
    setRegion(regionData.region);
    setCity(regionData.city || null);
  };

  const value = {
    region,
    city,
    setRegion: (regionData) => {
      setRegion(regionData.region);
      setCity(regionData.city || null);
    },
    openModal,
    locations,
    locationsLoading,
  };

  return (
    <RegionContext.Provider value={value}>
      {hasTriedToAutoLogin && children}
      <RegionSelectModal
        isOpen={isModalOpen}
        region={region}
        city={city}
        openModal={openModal}
        locations={locations}
        locationsLoading={locationsLoading}
        onClose={closeModal}
        onSelectRegion={handleRegionSelect}
      />
    </RegionContext.Provider>
  );
}
